import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { attendanceApi } from '../axios';
import { mockPunchDevices } from '../../mock/punch-logs.mock';
import { generatePunchLogsForEmployees } from '@/lib/attendance-mock-generator';
import { format, subDays } from 'date-fns';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function applyClientFilters(logs, params) {
  let data = [...logs];
  if (params.date)        data = data.filter(p => p.date === params.date);
  if (params.date_from)   data = data.filter(p => p.date >= params.date_from);
  if (params.date_to)     data = data.filter(p => p.date <= params.date_to);
  if (params.employee_id) data = data.filter(p => p.employee_id === params.employee_id);
  if (params.device_id)   data = data.filter(p => p.device_id === params.device_id);
  if (params.status)      data = data.filter(p => p.status === params.status);
  if (params.punch_type)  data = data.filter(p => p.punch_type === params.punch_type);
  if (params.department)  data = data.filter(p => p.department === params.department);
  if (params.search) {
    const q = params.search.toLowerCase();
    data = data.filter(p =>
      p.employee_name.toLowerCase().includes(q) ||
      p.employee_code.toLowerCase().includes(q)
    );
  }
  // Sort DESC by punch_time (matches backend sort order)
  data.sort((a, b) => b.punch_time.localeCompare(a.punch_time));
  return data;
}

function buildMeta(data, params) {
  const limit = Number(params.limit) || 50;
  const page  = Number(params.page)  || 1;
  return { total: data.length, page, limit, pages: Math.ceil(data.length / limit) };
}

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * List punch logs with optional filters.
 * Generated from REAL employees (passed as `employees`) so the company's actual
 * roster drives the data — no hardcoded demo names.
 *
 * Supported params: date, date_from, date_to, employee_id, device_id,
 *                   status, punch_type, department, search, page, limit
 *
 * @param {object} params    - filter/pagination params
 * @param {Array}  employees - from useEmployees() — drives the generated fallback
 */
export const usePunchLogs = (params = {}, employees = []) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const generated = useMemo(() => {
    const dateFrom = params.date_from || format(subDays(new Date(), 29), 'yyyy-MM-dd');
    const dateTo   = params.date_to   || format(new Date(), 'yyyy-MM-dd');
    const raw      = generatePunchLogsForEmployees(employees, dateFrom, dateTo);
    const filtered = applyClientFilters(raw, params);
    return { success: true, data: filtered, meta: buildMeta(filtered, params) };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, JSON.stringify(params)]);

  return useQuery({
    queryKey: ['punch-logs', params, employees.map(e => e.id || e.employee_id).join(',')],
    queryFn: () =>
      attendanceApi
        .get('/v1/attendance/punch-logs', { params })
        .then(r => {
          // Backend starts empty in Phase 1B → use generated from real employees
          if (!r.data?.data?.length && employees.length > 0) return generated;
          return r.data;
        })
        .catch(() => generated),
    initialData: employees.length > 0 ? generated : undefined,
    initialDataUpdatedAt: 0,
    staleTime: 30_000,
    retry: 0,
  });
};

/**
 * Fetch the registered biometric devices.
 */
export const usePunchDevices = () => useQuery({
  queryKey: ['punch-devices'],
  queryFn: () =>
    attendanceApi
      .get('/v1/attendance/punch-logs/devices')
      .then(r => r.data)
      .catch(() => ({ success: true, data: mockPunchDevices })),
  initialData: { success: true, data: mockPunchDevices },
  initialDataUpdatedAt: 0,
  staleTime: 60_000,
  retry: 0,
});

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * Manually add a punch log entry (HR override).
 * POST /v1/attendance/punch-logs
 * Body: { employee_id, punch_type, punch_time, device_id?, source? }
 */
export const useAddPunchLog = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      attendanceApi.post('/v1/attendance/punch-logs', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['punch-logs'] });
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
};

/**
 * Delete (soft-delete) a punch log record.
 * DELETE /v1/attendance/punch-logs/:id
 */
export const useDeletePunchLog = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      attendanceApi.delete(`/v1/attendance/punch-logs/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['punch-logs'] });
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
};

/**
 * Trigger a manual sync pull from biometric devices.
 * POST /v1/attendance/punch-logs/sync
 * Body: { device_ids?: string[], date_range?: { from, to } }
 */
export const useSyncPunchLogs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload = {}) =>
      attendanceApi.post('/v1/attendance/punch-logs/sync', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['punch-logs'] });
    },
  });
};

/**
 * Process raw/unprocessed punch logs into attendance records.
 * POST /v1/attendance/punch-logs/process
 * Body: { date?: string }   — defaults to today on the service side
 */
export const useProcessPunchLogs = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload = {}) =>
      attendanceApi.post('/v1/attendance/punch-logs/process', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['punch-logs'] });
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
};
